import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.array.push.js";
import MfIcon from './MfIcon.vue';
/**
 * Botão base da biblioteca
 * @component
 */
export default {
  name: 'MfButton',
  components: {
    MfIcon
  },
  props: {
    /**
     * Cor do botão
     */
    color: {
      type: String,
      default: 'marca-0-l10'
    },
    /**
     * Label do botão
     */
    label: {
      type: String,
      default: ''
    },
    /**
     * Valor atribuido ao botão
     */
    value: {
      type: [String, Boolean, Number],
      default: ''
    },
    /**
     * Icon do botão
     */
    icon: {
      type: String,
      default: ''
    },
    /**
     * Valor que define se o icon do botão vai estar preenchido
     */
    fillIcon: {
      type: Boolean,
      default: false
    },
    /**
     * Define se o icon do botão vai se posicionar à direita ou à esquerda do label
     * Posição do icon do botão
     */
    iconPosition: {
      type: String,
      default: 'left',
      validator: value => ['right', 'left'].includes(value)
    },
    /**
     * Indica se o botão vai estar desabilitado
     */
    disabled: {
      type: Boolean,
      default: false
    },
    /**
     * Indica se o botão vai estar com bordas
     */
    outlined: {
      type: Boolean,
      default: false
    },
    /**
     * Remove a sombra da caixa de botão.
     */
    depressed: {
      type: Boolean,
      default: true
    },
    /**
     * Transforma o background em transparente e cor do botão vira a cor
     */
    text: {
      type: Boolean,
      default: false
    },
    /**
     * Indica se o botão vai ocupar o espaço inteiro
     */
    block: {
      type: Boolean,
      default: false
    },
    /**
     * Indica se o botão padrão terá a cor branca na fonte (não se aplica a outlined e text)
     */
    dark: {
      type: Boolean,
      default: true
    },
    /**
     * Indica se o botão está em estado de loading
     */
    loading: {
      type: Boolean,
      default: false
    },
    /**
     * Indica se o botão é do tipo icone
     */
    type: {
      type: String,
      default: '',
      validator: value => ['', 'icon'].includes(value)
    },
    /**
     * Tamanho do botão
     */
    size: {
      type: String,
      default: 'md',
      validator: value => ['', 'lg', 'md', 'sm', 'xs'].includes(value)
    },
    /**
     * Estado de ativo do botão
     */
    inputValue: {
      type: Boolean,
      default: false
    },
    /**
     * Preenchimento do ícone
     */
    fill: {
      type: Boolean,
      default: false
    },
    /**
     * Indica o content presente no badge acima do botão
     */
    badgeContent: {
      // Number E String, como no v2 (`mf-vue-components/.../MfButton.vue:213-216`). Consumidor real
      // manda rótulo ("3 produtos", em `ModalGraphqlSearchContent.vue:257`), e com o tipo estreito
      // isso virava aviso de prop inválida e badge que nunca aparecia.
      type: [Number, String],
      default: 0
    },
    /**
     * Indica a cor do badge acima do botão
     */
    badgeColor: {
      type: String,
      default: 'marca-0-l40'
    },
    /**
     * Stable DOM id for Userflow step targeting (data-userflow-id).
     */
    dataUserflowId: {
      type: String,
      default: ''
    }
  },
  emits: ['click', 'mousedown', 'mouseleave', 'mouseup'],
  computed: {
    /**
     * Mesma regra do v2 (`mf-vue-components/.../MfButton.vue:227`): mostra o badge para qualquer
     * conteúdo que não seja 0, vazio ou nulo. O `badgeContent > 0` de antes escondia todo rótulo em
     * texto, porque `'3 produtos' > 0` é falso.
     */
    temBadge() {
      return this.badgeContent !== 0 && this.badgeContent !== '' && this.badgeContent != null;
    },
    getButtonClass() {
      const classes = [`mf-button-${this.size}`];
      if (this.icon) {
        classes.push(`mf-button-${this.size}-${this.iconPosition}`);
      }
      return classes.join(' ');
    },
    getIconSize() {
      const sizes = {
        lg: 20,
        md: 18,
        sm: 16,
        xs: 16
      };
      return sizes[this.size];
    },
    getIconButtonSize() {
      const sizes = {
        lg: 28,
        md: 24,
        sm: 24,
        xs: 18
      };
      return sizes[this.size];
    },
    computedIconColor() {
      if (this.disabled) return '';
      return this.outlined || this.text ? this.color : 'branco';
    },
    /**
     *  O dark desabilitado deixa o botão totalmente branco, então quando está disabled devemos remover o dark
     *@return Boolean
     */
    getIsDark() {
      // O dark desabilitado deixa o botão totalmente branco, então quando está disabled devemos remover o dark
      return this.dark && !this.disabled;
    },
    getVuetifySize() {
      const sizes = {
        lg: 'large',
        md: 'default',
        sm: 'small',
        xs: 'x-small'
      };
      return sizes[this.size] || 'default';
    },
    getIconVariant() {
      if (this.text) return 'text';
      if (this.outlined) return 'outlined';
      if (this.depressed) return 'plain';
      return 'text';
    },
    getIconColor() {
      if (this.disabled) return '';
      return this.color;
    },
    getVariant() {
      if (this.text) return 'text';
      if (this.outlined) return 'outlined';
      return 'elevated';
    }
  },
  methods: {
    /**
     * Método de click do botão, realiza o emit do evento click
     * Remove o foco do botão para evitar focus ring persistente (Vuetify 3 / Vue 3)
     */
    clickButton(event) {
      if (event?.currentTarget?.blur) {
        event.currentTarget.blur();
      }
      /**
       * Evento de click do botão
       * @event click
       * @property {String|Boolean|Number}
       */
      if (!this.loading) {
        this.$emit('click', this.value);
      }
    }
  }
};